import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import {
  addNotes,
  deleteNotes,
  inScale,
  midiToNoteName,
  moveNotes,
  notePitches,
  noteDur,
  noteStart,
  noteVelocity,
  parseKey,
  patternLength,
  quantizeNotes,
  resizeNotes,
  setNotesVelocity,
  snapTo,
  stampChord,
  STAMP_CHORDS,
  timeSignatureOf,
  beatsPerBar,
  updatePattern,
  type SoundNote,
} from '../../lib/sound-doc.js';
import { soundPlayer } from '../../lib/sound-player.js';
import { soundGrid, useSound } from '../../state/sound.js';
import { useWorkspace } from '../../state/workspace.js';
import { IconButton, isTyping } from '../ui/controls.js';
import { PatternPicker } from './StepSequencer.js';

/**
 * Pattern note editor. Draw (D) adds notes (drag to set the length), Select
 * (V) picks/moves/box-selects, Chord (C) stamps a chord on the clicked root.
 * Drag a note's right edge to resize, right-click to delete, drag bars in the
 * velocity lane. Rows in the document's (or pattern's) key are highlighted.
 */

const MIN_PITCH = 21;
const MAX_PITCH = 108;
const ROW_H = 14;
const KEYS_W = 52;
const VEL_H = 72;

type Tool = 'draw' | 'select' | 'chord';

type Drag =
  | { kind: 'move'; x0: number; y0: number; dt: number; dp: number; indices: number[]; anchorPitch: number }
  | { kind: 'resize'; x0: number; dd: number; indices: number[] }
  | { kind: 'draw'; x0: number; t: number; p: number; d: number; chord: number[] | null }
  | { kind: 'box'; x0: number; y0: number; x1: number; y1: number; add: boolean }
  | { kind: 'vel'; indices: number[]; v: number };

const SNAPS: Array<{ label: string; beats: number }> = [
  { label: '1/1 bar', beats: 4 },
  { label: '1/4', beats: 1 },
  { label: '1/8', beats: 0.5 },
  { label: '1/16', beats: 0.25 },
  { label: '1/32', beats: 0.125 },
  { label: '1/8T', beats: 1 / 3 },
  { label: '1/16T', beats: 1 / 6 },
  { label: 'Off', beats: 0 },
];

function isBlack(midi: number): boolean {
  return [1, 3, 6, 8, 10].includes(((midi % 12) + 12) % 12);
}

export function PianoRoll() {
  const doc = useSound((s) => s.doc);
  const name = useSound((s) => s.activePattern);
  const selected = useSound((s) => s.notes);
  const [tool, setTool] = useState<Tool>('draw');
  const [snap, setSnap] = useState(0.25);
  const [drawLen, setDrawLen] = useState<number | null>(null);
  const [velocity, setVelocity] = useState(0.8);
  const [chordQuality, setChordQuality] = useState('');
  const [pxBeat, setPxBeat] = useState(72);
  const [showScale, setShowScale] = useState(true);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const velRef = useRef<HTMLDivElement | null>(null);

  const pattern = name ? doc?.patterns?.[name] : undefined;
  const ts = doc ? timeSignatureOf(doc) : ([4, 4] as [number, number]);
  const key = useMemo(() => parseKey(pattern?.scale ?? doc?.key), [pattern?.scale, doc?.key]);
  const lengthBeats = patternLength(pattern, ts);
  const gridW = Math.max(lengthBeats + beatsPerBar(ts), 8) * pxBeat;
  const gridH = (MAX_PITCH - MIN_PITCH + 1) * ROW_H;
  const notes = pattern?.notes ?? [];
  const noteLen = drawLen ?? (snap || 0.25);

  useEffect(() => {
    soundGrid.beats = snap || 0.0625;
  }, [snap]);

  // Tool keys (D / V / C) while the piano roll is shown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target) || useWorkspace.getState().view !== 'sound') return;
      const k = e.key.toLowerCase();
      if (k === 'd') setTool('draw');
      else if (k === 'v') setTool('select');
      else if (k === 'c') setTool('chord');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Scroll to the notes (or C4) when the pattern changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !doc || !name) return;
    const ps = notes.flatMap((n) => notePitches(n, key));
    const center = ps.length ? (Math.min(...ps) + Math.max(...ps)) / 2 : 60;
    el.scrollTop = Math.max(0, (MAX_PITCH - center) * ROW_H - el.clientHeight / 2);
    // Only when switching patterns.
  }, [name]);

  const pos = (clientX: number, clientY: number) => {
    const el = scrollRef.current!;
    const r = el.getBoundingClientRect();
    const x = clientX - r.left - KEYS_W + el.scrollLeft;
    const y = clientY - r.top + el.scrollTop;
    return { x, y, t: Math.max(0, x / pxBeat), p: Math.max(MIN_PITCH, Math.min(MAX_PITCH, MAX_PITCH - Math.floor(y / ROW_H))) };
  };
  const snapT = (t: number) => Math.max(0, snap ? snapTo(t, snap) : Math.round(t * 1000) / 1000);
  const floorT = (t: number) => Math.max(0, snap ? Math.floor(t / snap + 1e-9) * snap : Math.round(t * 1000) / 1000);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      let next: Drag = d;
      if (d.kind === 'move') {
        const dt = snapT((e.clientX - d.x0) / pxBeat + 1000) - snapT(1000);
        const dp = -Math.round((e.clientY - d.y0) / ROW_H);
        if (dp !== d.dp) soundPlayer.previewNote(d.anchorPitch + dp, 0.6, 0.2);
        next = { ...d, dt, dp };
      } else if (d.kind === 'resize') next = { ...d, dd: (e.clientX - d.x0) / pxBeat };
      else if (d.kind === 'draw') {
        const end = pos(e.clientX, e.clientY).t;
        const min = snap || 1 / 32;
        next = { ...d, d: Math.max(min, snap ? Math.ceil((end - d.t) / snap - 1e-9) * snap : end - d.t) };
      } else if (d.kind === 'box') {
        const p = pos(e.clientX, e.clientY);
        next = { ...d, x1: p.x, y1: p.y };
      } else if (d.kind === 'vel') {
        const el = velRef.current;
        if (el) {
          const r = el.getBoundingClientRect();
          next = { ...d, v: Math.max(0, Math.min(1, 1 - (e.clientY - r.top) / r.height)) };
        }
      }
      dragRef.current = next;
      setDrag(next);
    };
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (d) finish(d);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  });

  function begin(d: Drag, e: ReactPointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = d;
    setDrag(d);
  }

  function finish(d: Drag) {
    const s = useSound.getState();
    const cur = s.doc;
    if (!cur || !name || !cur.patterns?.[name]) return;
    if (d.kind === 'move') {
      if (!d.dt && !d.dp) return;
      const r = moveNotes(cur, name, d.indices, d.dt, d.dp);
      s.update(r.doc);
      s.selectNotes(r.indices);
    } else if (d.kind === 'resize') {
      const dd = snap ? Math.round(d.dd / snap) * snap : d.dd;
      if (!dd) return;
      s.update(resizeNotes(cur, name, d.indices, dd, snap || 1 / 32));
      const n0 = d.indices.length === 1 ? cur.patterns[name]!.notes?.[d.indices[0]!] : undefined;
      if (n0) setDrawLen(Math.max(snap || 1 / 32, noteDur(n0) + dd));
    } else if (d.kind === 'draw') {
      const pitches = d.chord ?? [d.p];
      const fresh: SoundNote[] = pitches.map((p) => ({ t: d.t, d: d.d, p: midiToNoteName(p), v: velocity }));
      const r = addNotes(cur, name, fresh);
      // Grow the pattern when a note is drawn past its end.
      const end = d.t + d.d;
      const len = patternLength(cur.patterns[name], ts);
      const next = end > len && typeof cur.patterns[name]!.length === 'number' ? updatePattern(r.doc, name, { length: Math.ceil(end / beatsPerBar(ts)) * beatsPerBar(ts) }) : r.doc;
      s.update(next);
      s.selectNotes(r.indices);
      setDrawLen(d.d);
    } else if (d.kind === 'box') {
      const x0 = Math.min(d.x0, d.x1);
      const x1 = Math.max(d.x0, d.x1);
      const y0 = Math.min(d.y0, d.y1);
      const y1 = Math.max(d.y0, d.y1);
      const hit: number[] = [];
      notes.forEach((n, i) => {
        const nx0 = noteStart(n) * pxBeat;
        const nx1 = (noteStart(n) + noteDur(n)) * pxBeat;
        for (const p of notePitches(n, key)) {
          const ny = (MAX_PITCH - p) * ROW_H;
          if (nx1 >= x0 && nx0 <= x1 && ny + ROW_H >= y0 && ny <= y1) {
            hit.push(i);
            break;
          }
        }
      });
      s.selectNotes(d.add ? [...selected, ...hit] : hit);
    } else if (d.kind === 'vel') {
      s.update(setNotesVelocity(cur, name, d.indices, d.v));
    }
  }

  if (!doc) return null;
  const s = useSound.getState();

  if (!name || !pattern) {
    return (
      <div className="snd-pianoroll">
        <div className="snd-view-tools">
          <PatternPicker />
        </div>
        <div className="pane-empty muted">No pattern open. Pick or create one (patterns hold the notes that clips play).</div>
      </div>
    );
  }

  const selSet = new Set(selected);
  const previewFor = (i: number, n: SoundNote): { t: number; d: number; dp: number } => {
    if (drag?.kind === 'move' && drag.indices.includes(i)) return { t: Math.max(0, noteStart(n) + drag.dt), d: noteDur(n), dp: drag.dp };
    if (drag?.kind === 'resize' && drag.indices.includes(i)) {
      const dd = snap ? Math.round(drag.dd / snap) * snap : drag.dd;
      return { t: noteStart(n), d: Math.max(snap || 1 / 32, noteDur(n) + dd), dp: 0 };
    }
    return { t: noteStart(n), d: noteDur(n), dp: 0 };
  };

  const rows = [];
  for (let p = MAX_PITCH; p >= MIN_PITCH; p--) {
    const inKey = showScale && key ? inScale(p, key) : false;
    const root = showScale && key && ((p - key.root) % 12 + 12) % 12 === 0;
    rows.push(
      <div key={p} className={`snd-pr-row${isBlack(p) ? ' is-black' : ''}${inKey ? ' in-key' : ''}${root ? ' is-root' : ''}`} style={{ top: (MAX_PITCH - p) * ROW_H, height: ROW_H }} />,
    );
  }

  const beatLines = [];
  const bpb = beatsPerBar(ts);
  for (let b = 0; b * pxBeat <= gridW; b += 1) {
    beatLines.push(<span key={b} className={`snd-pr-beat${b % bpb === 0 ? ' is-bar' : ''}`} style={{ left: b * pxBeat }} />);
    if (snap && snap < 1 && pxBeat * snap >= 10)
      for (let k = snap; k < 1 - 1e-9; k += snap) beatLines.push(<span key={`${b}-${k}`} className="snd-pr-sub" style={{ left: (b + k) * pxBeat }} />);
  }

  return (
    <div className="snd-pianoroll">
      <div className="snd-view-tools">
        <PatternPicker />
        <div className="tb-group segmented">
          <IconButton icon="pencil" title="Draw notes" shortcut="D" active={tool === 'draw'} onClick={() => setTool('draw')} />
          <IconButton icon="cursor" title="Select / move notes" shortcut="V" active={tool === 'select'} onClick={() => setTool('select')} />
          <IconButton icon="layers" title="Chord stamp" shortcut="C" active={tool === 'chord'} onClick={() => setTool('chord')} />
        </div>
        {tool === 'chord' && (
          <label className="snd-inline">
            <span className="muted">Chord</span>
            <select value={chordQuality} onChange={(e) => setChordQuality(e.target.value)} aria-label="Chord type">
              {STAMP_CHORDS.map((q) => (
                <option key={q} value={q}>
                  {q === '' ? 'maj' : q}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="snd-inline" title="Grid for drawing, moving and resizing">
          <span className="muted">Snap</span>
          <select value={String(snap)} onChange={(e) => setSnap(Number(e.target.value))} aria-label="Snap">
            {SNAPS.map((o) => (
              <option key={o.label} value={String(o.beats)}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="snd-inline" title="Velocity of new notes">
          <span className="muted">Vel</span>
          <input type="number" min={0} max={1} step={0.05} value={velocity} onChange={(e) => setVelocity(Math.max(0, Math.min(1, Number(e.target.value) || 0)))} aria-label="Velocity for new notes" />
        </label>
        <label className="snd-inline" title="Pattern length in beats">
          <span className="muted">Length</span>
          <input
            type="number"
            min={0.25}
            step={1}
            value={lengthBeats}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v > 0) s.update(updatePattern(doc, name, { length: v }));
            }}
            aria-label="Pattern length in beats"
          />
        </label>
        <button
          type="button"
          className="tb-btn"
          title="Quantize the selected notes (all notes when none is selected) to the snap grid"
          disabled={!snap || !notes.length}
          onClick={() => s.update(quantizeNotes(doc, name, selected.length ? selected : notes.map((_, i) => i), snap))}
        >
          Quantize
        </button>
        <button type="button" className={`tb-btn${showScale ? ' is-active' : ''}`} title={key ? `Highlight ${pattern.scale ?? doc.key}` : 'Set a key on the document (e.g. "A minor") to highlight its scale'} onClick={() => setShowScale(!showScale)} disabled={!key}>
          {key ? (pattern.scale ?? doc.key) : 'No key'}
        </button>
        <span className="bar-spacer" />
        <button type="button" className="tb-btn" aria-label="Zoom out" onClick={() => setPxBeat(Math.max(16, pxBeat / 1.4))}>
          −
        </button>
        <button type="button" className="tb-btn" aria-label="Zoom in" onClick={() => setPxBeat(Math.min(400, pxBeat * 1.4))}>
          +
        </button>
      </div>
      <div
        className="snd-pr-scroll"
        ref={scrollRef}
        onScroll={(e) => {
          if (velRef.current) velRef.current.scrollLeft = e.currentTarget.scrollLeft;
        }}
      >
        <div className="snd-pr-content" style={{ width: KEYS_W + gridW, height: gridH }}>
          <div className="snd-pr-keys" style={{ width: KEYS_W, height: gridH }} aria-hidden="true">
            {Array.from({ length: MAX_PITCH - MIN_PITCH + 1 }, (_, i) => {
              const p = MAX_PITCH - i;
              const inKey = showScale && key ? inScale(p, key) : true;
              return (
                <div
                  key={p}
                  className={`snd-pr-key${isBlack(p) ? ' is-black' : ''}${inKey ? '' : ' out-key'}`}
                  style={{ top: i * ROW_H, height: ROW_H }}
                  onPointerDown={() => soundPlayer.previewNote(p, velocity)}
                  title={midiToNoteName(p)}
                >
                  {p % 12 === 0 ? midiToNoteName(p) : ''}
                </div>
              );
            })}
          </div>
          <div
            className={`snd-pr-grid tool-${tool}`}
            style={{ left: KEYS_W, width: gridW, height: gridH }}
            role="grid"
            aria-label={`Notes of pattern ${name}`}
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              const p = pos(e.clientX, e.clientY);
              if (tool === 'select') {
                begin({ kind: 'box', x0: p.x, y0: p.y, x1: p.x, y1: p.y, add: e.shiftKey }, e);
                return;
              }
              const t = floorT(p.t);
              const chord = tool === 'chord' ? stampChord(p.p, chordQuality) : null;
              for (const m of chord ?? [p.p]) soundPlayer.previewNote(m, velocity);
              begin({ kind: 'draw', x0: e.clientX, t, p: p.p, d: noteLen, chord }, e);
            }}
          >
            {rows}
            {beatLines}
            <span className="snd-pr-end" style={{ left: lengthBeats * pxBeat }} title="Pattern end" />
            {notes.map((n, i) => {
              const pv = previewFor(i, n);
              return notePitches(n, key).map((p, k) => (
                <div
                  key={`${i}-${k}`}
                  className={`snd-note${selSet.has(i) ? ' is-selected' : ''}${n.chord ? ' is-chord' : ''}`}
                  style={{
                    left: pv.t * pxBeat,
                    top: (MAX_PITCH - (p + pv.dp)) * ROW_H,
                    width: Math.max(4, pv.d * pxBeat - 1),
                    height: ROW_H - 1,
                    opacity: 0.45 + 0.55 * (noteVelocity(n)),
                  }}
                  role="gridcell"
                  aria-selected={selSet.has(i)}
                  aria-label={`${n.chord ?? midiToNoteName(p)} at beat ${noteStart(n)}, ${noteDur(n)} beats, velocity ${noteVelocity(n)}`}
                  title={`${n.chord ? `${n.chord} ` : ''}${midiToNoteName(p)} t=${noteStart(n)} d=${noteDur(n)} v=${noteVelocity(n)}${n.lyric ? ` “${n.lyric}”` : ''}`}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    const cur = useSound.getState();
                    if (!cur.doc) return;
                    const victims = selSet.has(i) ? selected : [i];
                    cur.selectNotes([]);
                    cur.update(deleteNotes(cur.doc, name, victims));
                  }}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    let indices = selected;
                    if (e.shiftKey) {
                      indices = selSet.has(i) ? selected.filter((j) => j !== i) : [...selected, i];
                      s.selectNotes(indices);
                      e.stopPropagation();
                      return;
                    }
                    if (!selSet.has(i)) {
                      indices = [i];
                      s.selectNotes(indices);
                    }
                    soundPlayer.previewNote(p, noteVelocity(n));
                    begin({ kind: 'move', x0: e.clientX, y0: e.clientY, dt: 0, dp: 0, indices, anchorPitch: p }, e);
                  }}
                >
                  {k === 0 && <span className="snd-note-label">{n.lyric ?? n.chord ?? (pv.d * pxBeat > 34 ? midiToNoteName(p + pv.dp) : '')}</span>}
                  <span
                    className="snd-resize"
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      const indices = selSet.has(i) ? selected : [i];
                      if (!selSet.has(i)) s.selectNotes(indices);
                      begin({ kind: 'resize', x0: e.clientX, dd: 0, indices }, e);
                    }}
                  />
                </div>
              ));
            })}
            {drag?.kind === 'draw' &&
              (drag.chord ?? [drag.p]).map((p) => (
                <div key={`draw-${p}`} className="snd-note is-ghost" style={{ left: drag.t * pxBeat, top: (MAX_PITCH - p) * ROW_H, width: drag.d * pxBeat - 1, height: ROW_H - 1 }} />
              ))}
            {drag?.kind === 'box' && (
              <div
                className="snd-box"
                style={{ left: Math.min(drag.x0, drag.x1), top: Math.min(drag.y0, drag.y1), width: Math.abs(drag.x1 - drag.x0), height: Math.abs(drag.y1 - drag.y0) }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="snd-vel" ref={velRef} style={{ height: VEL_H }} aria-label="Velocity lane">
        <div className="snd-vel-content" style={{ width: KEYS_W + gridW, height: VEL_H }}>
          <span className="snd-vel-caption muted" style={{ width: KEYS_W }}>
            Velocity
          </span>
          {notes.map((n, i) => {
            const v = drag?.kind === 'vel' && drag.indices.includes(i) ? drag.v : (noteVelocity(n));
            return (
              <span
                key={i}
                className={`snd-vel-bar${selSet.has(i) ? ' is-selected' : ''}`}
                style={{ left: KEYS_W + noteStart(n) * pxBeat, height: Math.max(2, v * (VEL_H - 8)) }}
                title={`velocity ${Math.round(v * 100) / 100}`}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  const indices = selSet.has(i) ? selected : [i];
                  const r = e.currentTarget.parentElement!.getBoundingClientRect();
                  begin({ kind: 'vel', indices, v: Math.max(0, Math.min(1, 1 - (e.clientY - r.top) / r.height)) }, e);
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
